import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../services/api';

interface Page {
  id: number;
  token: string;
  title: string | null;
  original_filename: string;
  file_size_bytes: number;
  view_count: number;
  last_viewed_at: string | null;
  project_id: number | null;
  public_path: string;
  created_at: string | null;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const LiveHtmlTestPage: React.FC = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [title, setTitle] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const apiOrigin = useMemo(() => {
    // The api client uses /api/* relative paths — derive origin from window.
    if (typeof window !== 'undefined') return window.location.origin;
    return '';
  }, []);

  const fullUrl = (p: Page) => `${apiOrigin}${p.public_path}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/live-html-test/list');
      setPages(res.data?.pages || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doUpload = useCallback(
    async (file: File) => {
      setError(null);
      if (!file.name.toLowerCase().endsWith('.html') && !file.name.toLowerCase().endsWith('.htm')) {
        setError('please upload a .html or .htm file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`file too large — ${formatBytes(file.size)} (limit 5 MB)`);
        return;
      }
      setUploading(true);
      try {
        const form = new FormData();
        form.append('file', file);
        if (title.trim()) form.append('title', title.trim());
        const res = await api.post('/live-html-test/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        // Prepend the new row
        setPages((prev) => [
          {
            id: res.data.id,
            token: res.data.token,
            title: res.data.title,
            original_filename: res.data.original_filename,
            file_size_bytes: res.data.file_size_bytes,
            view_count: 0,
            last_viewed_at: null,
            project_id: null,
            public_path: res.data.public_path,
            created_at: res.data.created_at,
          },
          ...prev,
        ]);
        setTitle('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'upload failed');
      } finally {
        setUploading(false);
      }
    },
    [title],
  );

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const onDelete = async (page: Page) => {
    if (!window.confirm(`Delete "${page.title || page.original_filename}"? The live URL will stop working.`)) return;
    setError(null);
    try {
      await api.delete(`/live-html-test/${page.id}`);
      setPages((prev) => prev.filter((p) => p.id !== page.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'delete failed');
    }
  };

  const copyUrl = async (page: Page) => {
    try {
      await navigator.clipboard.writeText(fullUrl(page));
    } catch {
      window.prompt('Copy this URL:', fullUrl(page));
    }
  };

  const qrSrc = (page: Page) => {
    const url = encodeURIComponent(fullUrl(page));
    // public no-tracking QR endpoint — renders an SVG QR for any URL.
    return `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${url}`;
  };

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Live HTML Test</h1>
          <p className="mt-1 text-sm text-gray-600">
            Upload an HTML file to get a live, shareable URL. Show or scan it from any device — delete anytime.
          </p>
        </div>

        {/* Upload card */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mb-8 rounded-xl border-2 border-dashed transition-colors ${
            dragOver
              ? 'border-brand-500 bg-brand-50'
              : 'border-gray-300 bg-white hover:border-gray-400'
          }`}
        >
          <div className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-gray-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.9 5 5 0 019.9-1A4.5 4.5 0 0118 16M9 13l3 3m0 0l3-3m-3 3V8" />
                </svg>
                <span className="font-medium">Drop an .html file here or click to browse</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">Max 5 MB · static HTML/CSS/JS only</p>
              <div className="mt-3">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Optional title (defaults to filename)"
                  className="w-full sm:w-80 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".html,.htm,text/html"
                className="hidden"
                onChange={onFileChange}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading…' : 'Choose file'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-medium text-gray-900">Your live previews</h2>
            <button
              onClick={load}
              className="text-sm text-brand-600 hover:text-brand-700"
            >
              Refresh
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : pages.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              No live HTML previews yet. Upload one above.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {pages.map((p) => (
                <li key={p.id} className="p-5 flex flex-col sm:flex-row sm:items-start gap-4">
                  <img
                    src={qrSrc(p)}
                    alt={`QR code for ${p.title || p.original_filename}`}
                    width={120}
                    height={120}
                    className="rounded-md border border-gray-200 bg-white shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 truncate">
                        {p.title || p.original_filename}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {formatBytes(p.file_size_bytes)} · {p.view_count} view{p.view_count === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Uploaded {formatDate(p.created_at)}
                      {p.last_viewed_at && <> · last viewed {formatDate(p.last_viewed_at)}</>}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <code className="flex-1 min-w-0 truncate rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
                        {fullUrl(p)}
                      </code>
                      <button
                        onClick={() => copyUrl(p)}
                        className="px-2.5 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                      >
                        Copy
                      </button>
                      <a
                        href={fullUrl(p)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2.5 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                      >
                        Open
                      </a>
                      <button
                        onClick={() => onDelete(p)}
                        className="px-2.5 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LiveHtmlTestPage;
